import { useState, useEffect } from 'react';
import { Building2, Package, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import Layout from '../components/shared/Layout';
import MetricCard from '../components/dashboard/MetricCard';
import ComplianceGauge from '../components/dashboard/ComplianceGauge';
import { useAuth } from '../hooks/useAuth';
import { apiGet } from '../config/api';

export default function DashboardPage() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [metrics, setMetrics] = useState({
        facilities: 0,
        equipment: 0,
        overdueInspections: 0,
        compliantEquipment: 0,
        overallCompliance: 0,
        labourCodeCompliance: 0,
        apsadCompliance: 0,
    });

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const res = await apiGet('/api/dashboard');
                const data = await res.json();
                if (data.success && data.stats) {
                    setMetrics(data.stats);
                }
            } catch (error) {
                console.error('Failed to fetch dashboard metrics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Upcoming tasks from real inspections
    const [upcomingTasks, setUpcomingTasks] = useState<{
        id: string;
        title: string;
        equipment: string;
        dueDate: string;
        priority: 'high' | 'medium' | 'low';
    }[]>([]);

    useEffect(() => {
        const fetchUpcomingTasks = async () => {
            try {
                const res = await apiGet('/api/inspections');
                const data = await res.json();
                if (data.success && data.inspections) {
                    const now = new Date();
                    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

                    const upcoming = data.inspections
                        .filter((insp: any) => {
                            const dueDate = new Date(insp.nextDueDate);
                            return dueDate >= now && dueDate <= thirtyDaysFromNow;
                        })
                        .sort((a: any, b: any) => new Date(a.nextDueDate).getTime() - new Date(b.nextDueDate).getTime())
                        .slice(0, 5) // Top 5
                        .map((insp: any) => {
                            const dueDate = new Date(insp.nextDueDate);
                            const daysUntil = Math.ceil((dueDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

                            let priority: 'high' | 'medium' | 'low' = 'low';
                            if (daysUntil <= 3) priority = 'high';
                            else if (daysUntil <= 7) priority = 'medium';

                            const dueDateStr = daysUntil === 0 ? 'Today' :
                                daysUntil === 1 ? '1 day' :
                                    daysUntil <= 7 ? `${daysUntil} days` :
                                        `${Math.ceil(daysUntil / 7)} weeks`;

                            return {
                                id: insp.id,
                                title: `${insp.inspectionType} Inspection`,
                                equipment: `${insp.equipmentType || 'Equipment'} - ${insp.equipmentNumber}`,
                                dueDate: dueDateStr,
                                priority
                            };
                        });

                    setUpcomingTasks(upcoming);
                }
            } catch (error) {
                console.error('Failed to fetch upcoming tasks:', error);
            }
        };

        fetchUpcomingTasks();
    }, []);

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'border-l-red-500 bg-red-50';
            case 'medium':
                return 'border-l-alert-500 bg-alert-50';
            default:
                return 'border-l-steel-400 bg-steel-50';
        }
    };

    return (
        <Layout>
            <div className="min-h-screen p-8">
                {/* Header */}
                <div className="mb-8 animate-fadeIn">
                    <h1 className="text-display-1 font-bold text-charcoal-950 mb-2">
                        Welcome back, {user?.fullName?.split(' ')[0] || 'User'} 👋
                    </h1>
                    <p className="text-steel-600">
                        Here's your compliance overview for today
                    </p>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <MetricCard
                        title="Total Facilities"
                        value={metrics.facilities}
                        subtitle="Across all locations"
                        icon={Building2}
                        color="ember"
                        trend={{ value: 12, direction: 'up' }}
                        delay={0}
                    />
                    <MetricCard
                        title="Equipment Inventory"
                        value={metrics.equipment}
                        subtitle={`${metrics.compliantEquipment} compliant`}
                        icon={Package}
                        color="steel"
                        trend={{ value: 8, direction: 'up' }}
                        delay={100}
                    />
                    <MetricCard
                        title="Compliant Equipment"
                        value={metrics.compliantEquipment}
                        subtitle={`${((metrics.compliantEquipment / metrics.equipment) * 100).toFixed(0)}% of total`}
                        icon={CheckCircle2}
                        color="safety"
                        trend={{ value: 5, direction: 'up' }}
                        delay={200}
                    />
                    <MetricCard
                        title="Overdue Inspections"
                        value={metrics.overdueInspections}
                        subtitle="Require immediate attention"
                        icon={AlertCircle}
                        color="alert"
                        trend={{ value: 15, direction: 'down' }}
                        delay={300}
                    />
                </div>

                {/* Compliance Gauges */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                    <ComplianceGauge
                        percentage={metrics.overallCompliance}
                        title="Overall Compliance"
                        subtitle="All facilities combined"
                    />
                    <ComplianceGauge
                        percentage={metrics.labourCodeCompliance}
                        title="Labour Code R4227-29"
                        subtitle="Water spray requirements"
                    />
                    <ComplianceGauge
                        percentage={metrics.apsadCompliance}
                        title="APSAD R4 Standard"
                        subtitle="General protection coverage"
                    />
                </div>

                {/* Upcoming Tasks */}
                <div className="card-glass p-6 animate-slideUp" style={{ animationDelay: '400ms' }}>
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Clock className="w-6 h-6 text-ember-500" />
                            <h2 className="text-xl font-display font-bold text-charcoal-950">
                                Upcoming Tasks
                            </h2>
                        </div>
                        <button className="text-sm text-ember-600 hover:text-ember-700 font-medium transition-colors">
                            View All →
                        </button>
                    </div>

                    <div className="space-y-3">
                        {upcomingTasks.map((task) => (
                            <div
                                key={task.id}
                                className={`p-4 border-l-4 rounded-r-xl ${getPriorityColor(task.priority)} transition-all duration-200 hover:shadow-md cursor-pointer`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-charcoal-900 mb-1">
                                            {task.title}
                                        </h4>
                                        <p className="text-sm text-steel-600">{task.equipment}</p>
                                    </div>
                                    <div className="text-right">
                                        <span className="inline-block px-3 py-1 bg-white rounded-lg text-sm font-medium text-steel-700 shadow-sm">
                                            Due in {task.dueDate}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {upcomingTasks.length === 0 && (
                        <div className="text-center py-12">
                            <CheckCircle2 className="w-12 h-12 text-safety-500 mx-auto mb-3" />
                            <p className="text-steel-600">All caught up! No pending tasks.</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}
